import React from 'react';
import { Typography, Flex, theme, Divider } from 'antd';

import KPIPagesCrawled, {
  type KPIPagesCrawledProps,
  TOOLTIP_MESSAGE as KPIPAGES_CRAWLED_TOOLTIP_MESSAGE,
} from './KPIPagesCrawled';
import KPICacheHitsRatio, {
  type KPICacheHitsRatioProps,
  TOOLTIP_MESSAGE as KPICACHE_HITS_RATIO_TOOLTIP_MESSAGE,
} from './KPICacheHitsRatio';
import KPIErrorRate, { type KPIErrorRateProps, TOOLTIP_MESSAGE as KPIERROR_RATE_TOOLTIP_MESSAGE } from './KPIErrorRate';
import KPIAverageCrawlTime, {
  type KPIAverageCrawlTimeProps,
  TOOLTIP_MESSAGE as KPIAVERAGE_CRAWL_TIME_TOOLTIP_MESSAGE,
} from './KPIAverageCrawlTime';
import { FiltersForPrint } from '~components/Filters';
import ChartPagesCrawled, {
  type ChartPagesCrawledProps,
  TOOLTIP_MESSAGE as CHART_PAGES_CRAWLED_TOOLTIP_MESSAGE,
} from './ChartPagesCrawled';
import ChartCrawlerType, {
  type ChartCrawlerTypeProps,
  TOOLTIP_MESSAGE as CHART_CRAWLER_TYPE_TOOLTIP_MESSAGE,
} from './ChartCrawlerType';
import ChartAverageCrawlTime, {
  type ChartAverageCrawlTimeProps,
  TOOLTIP_MESSAGE as CHART_AVERAGE_CRAWL_TIME_TOOLTIP_MESSAGE,
} from './ChartAverageCrawlTime';
import ChartStatusCodes, {
  type ChartStatusCodesProps,
  TOOLTIP_MESSAGE as CHART_STATUS_CODES_TOOLTIP_MESSAGE,
} from './ChartStatusCodes';
import ChartPagesRendered, {
  type ChartPagesRenderedProps,
  getPagesRenderedTooltipMessage,
} from './ChartPagesRendered';
import { IsPrintingProvider } from '~library/print';
import { purifyHtml } from '~library/purifyHtml';
import { Page, PageContent } from '~components/PrintableLayout';

type PrintableDashboardProps = {
  filters: {
    domain?: string;
    crawlers?: string[];
    dateFrom: Date;
    dateTo: Date;
    dateRange: [Date, Date];
  };
  emptyState?: React.ReactNode;
  kpis: {
    pagesCrawled: KPIPagesCrawledProps;
    cacheHitsRatio: KPICacheHitsRatioProps;
    errorRate: KPIErrorRateProps;
    averageCrawlTime: KPIAverageCrawlTimeProps;
  };
  charts: {
    pagesCrawled: Omit<ChartPagesCrawledProps, 'dateRange'>;
    crawlerType: Omit<ChartCrawlerTypeProps, 'dateRange'>;
    averageCrawlTime: Omit<ChartAverageCrawlTimeProps, 'dateRange'>;
    statusCodes: Omit<ChartStatusCodesProps, 'dateRange'>;
    pagesRendered: Omit<ChartPagesRenderedProps, 'dateRange' | 'onIncludeScheduledRendersChange'>;
  };
};

function PrintableDashboard({
  filters,
  emptyState,
  kpis,
  charts,
}: PrintableDashboardProps) {
  const { token } = theme.useToken();
  const onIncludeScheduledRendersChange = React.useCallback(() => undefined, []);

  return (
    <IsPrintingProvider initialValue={true}>
      <Page>
        <PageContent>
          <FiltersForPrint
            domainSelected={filters.domain}
            crawlersSelected={filters.crawlers || []}
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
          />

          {emptyState ? (
            emptyState
          ) : (
            <>
              <Divider />
              <Typography.Title level={4} style={{ color: token.colorTextTertiary, marginBottom: token.marginMD }}>
                KPIs
              </Typography.Title>
              <div>
                <Typography.Paragraph>
                  <Typography.Text strong>Pages Crawled: </Typography.Text>
                  <span dangerouslySetInnerHTML={{ __html: purifyHtml(KPIPAGES_CRAWLED_TOOLTIP_MESSAGE) }} />
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <Typography.Text strong>Cache Hits Ratio: </Typography.Text>
                  <span dangerouslySetInnerHTML={{ __html: purifyHtml(KPICACHE_HITS_RATIO_TOOLTIP_MESSAGE) }} />
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <Typography.Text strong>Error Rate: </Typography.Text>
                  <span dangerouslySetInnerHTML={{ __html: purifyHtml(KPIERROR_RATE_TOOLTIP_MESSAGE) }} />
                </Typography.Paragraph>
                <Typography.Paragraph>
                  <Typography.Text strong>Average Crawl Time: </Typography.Text>
                  <span dangerouslySetInnerHTML={{ __html: purifyHtml(KPIAVERAGE_CRAWL_TIME_TOOLTIP_MESSAGE) }} />
                </Typography.Paragraph>
              </div>
              <Flex gap="middle" style={{ overflowX: 'auto' }} wrap="wrap" justify="center">
                <KPIPagesCrawled {...kpis.pagesCrawled} />
                <KPICacheHitsRatio {...kpis.cacheHitsRatio} />
                <KPIErrorRate {...kpis.errorRate} />
                <KPIAverageCrawlTime {...kpis.averageCrawlTime} />
              </Flex>
            </>
          )}
        </PageContent>
      </Page>

      {!emptyState && (
        <>
          <Page>
            <PageContent>
              <Typography.Title level={4} style={{ color: token.colorTextTertiary, marginBottom: token.marginMD }}>
                Crawler Activity
              </Typography.Title>

              <Typography.Paragraph>
                <Typography.Text strong>Pages Crawled: </Typography.Text>
                <span dangerouslySetInnerHTML={{ __html: purifyHtml(CHART_PAGES_CRAWLED_TOOLTIP_MESSAGE) }} />
              </Typography.Paragraph>
              <ChartPagesCrawled
                dateRange={filters.dateRange}
                rawData={charts.pagesCrawled.rawData}
                isLoading={charts.pagesCrawled.isLoading}
              />
            </PageContent>
          </Page>

          <Page>
            <PageContent>
              <Typography.Paragraph>
                <Typography.Text strong>Crawler Type: </Typography.Text>
                <span dangerouslySetInnerHTML={{ __html: purifyHtml(CHART_CRAWLER_TYPE_TOOLTIP_MESSAGE) }} />
              </Typography.Paragraph>
              <ChartCrawlerType
                dateRange={filters.dateRange}
                rawData={charts.crawlerType.rawData}
                isLoading={charts.crawlerType.isLoading}
              />
            </PageContent>
          </Page>

          <Page>
            <PageContent>
              <div className="printable-page__space">
                <Typography.Paragraph>
                  <Typography.Text strong>Average Crawl Time: </Typography.Text>
                  <span dangerouslySetInnerHTML={{ __html: purifyHtml(CHART_AVERAGE_CRAWL_TIME_TOOLTIP_MESSAGE) }} />
                </Typography.Paragraph>
                <ChartAverageCrawlTime
                  dateRange={filters.dateRange}
                  rawData={charts.averageCrawlTime.rawData}
                  isLoading={charts.averageCrawlTime.isLoading}
                />
              </div>
              <Typography.Paragraph>
                <Typography.Text strong>Status Codes: </Typography.Text>
                <span dangerouslySetInnerHTML={{ __html: purifyHtml(CHART_STATUS_CODES_TOOLTIP_MESSAGE) }} />
              </Typography.Paragraph>
              <ChartStatusCodes
                dateRange={filters.dateRange}
                rawData={charts.statusCodes.rawData}
                isLoading={charts.statusCodes.isLoading}
              />
            </PageContent>
          </Page>

          <Page>
            <PageContent>
              <Typography.Title level={4} style={{ color: token.colorTextTertiary, marginBottom: token.marginMD }}>
                Performance & Health
              </Typography.Title>

              <Typography.Paragraph>
                <Typography.Text strong>Pages Rendered: </Typography.Text>
                <span
                  dangerouslySetInnerHTML={{
                    __html: purifyHtml(getPagesRenderedTooltipMessage(charts.pagesRendered.includeScheduledRenders)),
                  }}
                />
              </Typography.Paragraph>
              <ChartPagesRendered
                dateRange={filters.dateRange}
                rawData={charts.pagesRendered.rawData}
                isLoading={charts.pagesRendered.isLoading}
                includeScheduledRenders={charts.pagesRendered.includeScheduledRenders}
                onIncludeScheduledRendersChange={onIncludeScheduledRendersChange}
              />
            </PageContent>
          </Page>
        </>
      )}
    </IsPrintingProvider>
  );
}

export default PrintableDashboard;
